import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { sortBy } from 'lodash';
import Downshift from 'downshift';
import {
  withStyles,
  TextField,
  Paper,
  Icon,
  Divider,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItem,
  Typography,
} from '@material-ui/core';

import Tag from '../../widgets/Tag';
import FieldDisplayMessage from '../../widgets/FieldDisplayMessage';
import CreateTagInline from './CreateTagInline';
import styles from './styles';

export const getColorForTagType = (tagTypes, tagTypeId) => {
  const tagType = tagTypes.filter((i) => i.id === tagTypeId)[0];
  return tagType === undefined
    ? ''
    : tagType.iconColor;
};

export const getIconForTagType = (tagTypes, tagTypeId) => {
  const tagType = tagTypes.filter((i) => i.id === tagTypeId)[0];
  return tagType === undefined
    ? ''
    : tagType.icon;
};

const renderInput = (inputProps) => {
  const {
    InputProps,
    classes,
    readonly,
    helperText,
    selectedItem,
    availableItems,
    label,
  } = inputProps;

  const allItemSelected = selectedItem.length === availableItems.length;

  return (
    <TextField
      fullWidth
      variant="outlined"
      InputLabelProps={{ shrink: (selectedItem.length > 0 || inputProps.InputProps.value.length > 0) }}
      label={
        allItemSelected
          ? 'No Tags Available'
          : label
      }
      disabled={readonly || allItemSelected}
      helperText={helperText || ' '}
      InputProps={{
        autoComplete: 'off',
        classes: {
          root: classes.inputRoot,
          input: classes.inputInput,
        },
        ...InputProps,
      }}
      autoComplete="off"
    />
  );
};

const renderChipList = (inputProps, classes) => {
  const {
    selectedItem,
    onRemoveItem,
    tagTypes,
    readonly,
  } = inputProps;

  return (
    <span className={classes.chipContainer}>
      {
        selectedItem.length > 0
          && selectedItem
            .filter((item) => item.id)
            .sort((a, b) => a.tagType.id - b.tagType.id)
            .map((item) => (
              <Tag
                key={`tag-${item.id}`}
                tag={item}
                icon={getIconForTagType(tagTypes, item.tagType.id)}
                color={getColorForTagType(tagTypes, item.tagType.id)}
                onAction={() => onRemoveItem(item)}
                showAction={!readonly}
              />
            ))
        }
    </span>
  );
};

const renderSuggestion = (params) => {
  const {
    item,
    itemProps,
    selectedItem: selectedItems,
    tagTypes,
    toggleMenu,
  } = params;

  const isSelected = selectedItems.filter((selectedItem) => selectedItem.id === item.id).length >= 1;

  return (
    <ListItem
      dense
      button
      value={item}
      {...itemProps}
      onClick={(e) => {
        itemProps.onClick(e);
        toggleMenu();
      }}
      key={item.id}
      component="div"
      divider
    >
      <span style={{ marginTop: '.5em' }}>
        <Tag
          tag={item}
          icon={getIconForTagType(tagTypes, item.tagType.id)}
          color={getColorForTagType(tagTypes, item.tagType.id)}
          onAction={() => {}}
          showAction={false}
        />
      </span>
      {
        isSelected
        && (
          <ListItemSecondaryAction>
            <Icon fontSize="small" edge="end" color="primary">check_circle</Icon>
          </ListItemSecondaryAction>
        )
      }
    </ListItem>
  );
};

const getSuggestions
  = (inputValue, itemList) => itemList.filter((item) => item.name.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0);

class TagDownshiftComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = { showAddTagForm: false };
  }

  toggleShowAddTagForm = () => {
    this.setState((prevState) => ({ showAddTagForm: !prevState.showAddTagForm }));
  }

  render() {
    const {
      classes,
      availableItems,
      onRemoveItem,
      tagTypes,
      readonly,
      helperText,
      isAdmin,
      label,
      ...rest
    } = this.props;

    const { showAddTagForm } = this.state;

    return (
      availableItems
        && availableItems.length > 0
        ? (
          <Downshift {...rest}>
            {
              ({
                getInputProps,
                getItemProps,
                inputValue,
                selectedItem,
                toggleMenu,
                isOpen,
              }) => (
                <div>
                  {
                    showAddTagForm
                      ? (
                        <CreateTagInline
                          tagTypes={tagTypes}
                          onCancelAddTag={this.toggleShowAddTagForm}
                          inputValue={inputValue}
                          onAddTag={(newTag) => {
                            getItemProps({ item: newTag }).onClick({ target: { value: newTag } });
                          }}
                        />
                      )
                      : (
                        renderInput({
                          classes,
                          readonly,
                          helperText,
                          selectedItem,
                          availableItems,
                          label,
                          InputProps: {
                            ...getInputProps({ onClick: () => toggleMenu() }),
                            startAdornment: renderChipList({
                              onRemoveItem,
                              selectedItem,
                              tagTypes,
                              readonly,
                            }, classes),
                          },
                        })
                      )
                  }

                  {
                    isOpen
                      && !readonly
                      && (
                        <Paper className={classes.paper} style={{ width: '90%' }} square>
                          {
                            tagTypes.map((tagType) => {
                              const tags = sortBy(getSuggestions(
                                inputValue,
                                availableItems.filter((availableItem) => availableItem.tagType.id === tagType.id),
                              ), ['name']);
                              return (
                                tags.length > 0
                                  ? (
                                    <div key={`menu-item-tag-type-${tagType.id}`}>
                                      <ListItem className={classes.greyBackground}>
                                        <Typography variant="caption">{tagType.name}</Typography>
                                      </ListItem>
                                      {
                                        tags.map((item) => renderSuggestion({
                                          item,
                                          itemProps: getItemProps({ item }),
                                          selectedItem,
                                          tagTypes,
                                          toggleMenu,
                                        }))
                                      }
                                    </div>
                                  )
                                  : <div key={`menu-item-tag-type-${tagType.id}`} />
                              );
                            })
                          }
                          {
                            isAdmin
                              && (
                                <span>
                                  <Divider />
                                  <ListItem
                                    onClick={() => {
                                      toggleMenu();
                                      this.toggleShowAddTagForm();
                                    }}
                                    dense
                                    button
                                    className={classes.greyBackground}
                                  >
                                    <ListItemIcon>
                                      <Icon color="primary">add</Icon>
                                    </ListItemIcon>
                                    <Typography variant="inherit">
                                      Create new tag
                                    </Typography>
                                  </ListItem>
                                </span>
                              )
                          }
                        </Paper>
                      )
                  }
                </div>
              )
            }
          </Downshift>
        )
        : (
          <FieldDisplayMessage
            title="No Tags defined for your organization"
            subTitle={isAdmin
              ? <Link to="/settings/events?tab=2" href="/settings/events?tab=2">Create tags now</Link>
              : 'Ask your organization administrator to create tags from the Event Settings page'}
            icon="info_outlined"
            disableGutters
          />
        )
    );
  }
}

TagDownshiftComponent.propTypes = {
  classes: PropTypes.object,
  availableItems: PropTypes.array,
  onRemoveItem: PropTypes.func,
  tagTypes: PropTypes.array,
  readonly: PropTypes.bool,
  helperText: PropTypes.string,
  isAdmin: PropTypes.bool,
  label: PropTypes.string,
};

TagDownshiftComponent.defaultProps = {
  classes: {},
  availableItems: [],
  onRemoveItem: () => {},
  tagTypes: [],
  readonly: false,
  helperText: null,
  isAdmin: false,
  label: 'Select Tags',
};

export default withStyles(styles)(TagDownshiftComponent);
